/**
 * ADR-0099 — the tier-entitlement SOT: which paid perks each billing tier is
 * entitled to, as SOLD on the public pricing page. Client surfaces (dashboard
 * upsells, pricing cards) and server enforcement (delivery-payload branding)
 * both read this registry so the sold ladder and the enforced ladder can
 * never fork. Keys are in lockstep with `UC_TIERS` (test-locked).
 *
 * Deliberately narrow at v1: `brandingRemoval` is the only entitlement here
 * because it is the only one that is server-ENFORCED. The other Growth-bundle
 * perks (MCP server, multilingual, BYOK — ADR-0088 §3) are live for every
 * tier today as a deliberate launch posture (over-delivery, dispositioned in
 * ADR-0099 §4) — encoding them as tier-gated here while the server serves
 * them to everyone would make this registry lie. Add a field ONLY when its
 * enforcement ships in the same change.
 *
 * Pure data + pure functions only (packages/shared discipline).
 */

export interface TierEntitlements {
  /**
   * May the "Powered by Syncanix" widget branding be removed? Sold on the
   * Growth card ("priority support · branding removed"); growth+ per
   * ADR-0099 (supersedes the AF-209 Enterprise-only posture, which shipped
   * no enforcement and contradicted the public promise).
   */
  readonly brandingRemoval: boolean;
  /**
   * May Channel B (server-side MCP execution against the customer's API) be
   * ENABLED? Growth-and-above per the operator's authoritative late
   * 2026-06-11 answer (supersedes the earlier "unsold — no gate" posture in
   * the A16 design archive). Server-enforced in
   * `ChannelBAdminService.setConfig` (UPGRADE_REQUIRED below Growth);
   * `disable` is deliberately UN-gated — a downgraded workspace can always
   * turn it off.
   */
  readonly channelB: boolean;
  /**
   * Conversation-data retention window in days — the PUBLISHED per-tier
   * table on the live privacy page (`privacy.retention.*`: Starter
   * 30 / Growth 90 / Scale 365 / Enterprise default 90, customer-
   * configurable later). Enforced by the daily retention purge in the DSAR
   * fulfillment worker (C26-S5) — shipped in the same change per this
   * registry's add-with-enforcement rule. Per-tenant shorten/extend
   * (the dashboard control) is the documented roadmap follow-up.
   */
  readonly conversationRetentionDays: number;
  /**
   * NOTE (ADR-0464): Frontier is no longer a tier-gated entitlement. It was
   * a `frontier: boolean` field here (bundled into Growth+ per ADR-0182);
   * that field and its tier-rung enforcement were REMOVED when Frontier became
   * UNIVERSAL — on for every tenant, billed via normal usage (Usage Credits),
   * no separate entitlement. Do not re-add a Frontier field here: the
   * resolution ladder (`FrontierStateService.resolveFrontierState`) grants it
   * to everyone and no longer reads this registry.
   */
  /**
   * May hosted (Syncanix-managed, keyless) catalog enrichment be RUN? Operator
   * funding decision on SYN-51 (2026-07-18, ADR-0159 Acceptance record):
   * managed enrichment is bundled into every subscription tier — there is no
   * free tier, and non-paying tenants resolve to `starter` (itself `true`), so
   * this flag **segments nothing today**: every resolvable tier is entitled. It
   * exists purely as the server-enforcement SEAM (`EnrichCatalogService.start`
   * throws UPGRADE_REQUIRED when a row is `false`) so a future free tier can be
   * gated without a new enforcement change — per this registry's
   * add-with-enforcement rule. Spend is bounded by the dedicated per-tenant
   * enrichment allowance (`ENRICHMENT_UC_MONTHLY_ALLOWANCE`) + the single-run
   * lock + content-hash dedupe, not by this flag.
   */
  readonly enrichment: boolean;
  /**
   * May a white-label CUSTOM DOMAIN be configured + verified? Sold as the
   * white-label tier (`chat.<customer-domain>` instead of the shared
   * `widget.syncanix.com`); Growth-and-above per the operator decision
   * (2026-07-22), consistent with the other Growth-bundle perks. Server-
   * ENFORCED in `CustomDomainService.setCustomDomain` / `verifyCustomDomain`
   * (UPGRADE_REQUIRED below Growth) — shipped in the SAME change per this
   * registry's add-with-enforcement rule; `removeCustomDomain` / read stay
   * un-gated so a downgraded workspace can always see + remove what it can no
   * longer configure.
   */
  readonly customDomain: boolean;
}

export const TIER_ENTITLEMENTS = {
  starter: {
    brandingRemoval: false,
    channelB: false,
    conversationRetentionDays: 30,
    enrichment: true,
    customDomain: false,
  },
  growth: {
    brandingRemoval: true,
    channelB: true,
    conversationRetentionDays: 90,
    enrichment: true,
    customDomain: true,
  },
  scale: {
    brandingRemoval: true,
    channelB: true,
    conversationRetentionDays: 365,
    enrichment: true,
    customDomain: true,
  },
  enterprise: {
    brandingRemoval: true,
    channelB: true,
    conversationRetentionDays: 90,
    enrichment: true,
    customDomain: true,
  },
} as const satisfies Record<string, TierEntitlements>;

export type EntitlementTier = keyof typeof TIER_ENTITLEMENTS;

/** The entitlement row for `tier`. */
export function entitlementsFor(tier: EntitlementTier): TierEntitlements {
  return TIER_ENTITLEMENTS[tier];
}
